// File: src/pages/QuizJourda.js
// -----------------------------------------------------------------------------
// Page questionnaire : Jourda - Préférences professionnelles
// -----------------------------------------------------------------------------
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import IntroScreen from '../components/questionnaires/IntroScreen';
import QuestionScreen from '../components/questionnaires/QuestionScreen';
import JourdaResultsScreen from '../components/questionnaires/JourdaResultsScreen';
import PremiumModal from '../components/premium/PremiumModal';
import { getJourdaUserResult, getJourdaUserAnswers, getJourdaSurvey, createJourdaUserResult, resetJourdaSurvey } from '../api/questionnaires';
import { jourdaIntroConfig } from '../data/introConfigs';
import { jourdaQuestionConfig } from '../data/questionScreenConfigs';
import { HistoryContext } from "../context/HistoryContext";
import { QUIZZES_BY_ID } from '../data/quizzes';

export default function QuizJourda() {
  const { user, updateUser } = useContext(AuthContext);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [screen, setScreen] = useState('loading');
  const [results, setResults] = useState(null);
  const [existingAnswers, setExistingAnswers] = useState([]);
  const [questions, setQuestions] = useState([]);
  const prenom = user?.firstName || user?.name || 'Toi';
  const { addAction } = useContext(HistoryContext);

  // Vérifier l'état du questionnaire au chargement
  useEffect(() => {
    async function checkStatus() {
      try {
        // Charger en parallèle : résultats, réponses existantes et questions
        const [resultData, answersData, questionsData] = await Promise.all([
          getJourdaUserResult(),
          getJourdaUserAnswers(),
          getJourdaSurvey()
        ]);

        setQuestions(questionsData);
        setExistingAnswers(answersData || []);

        if (resultData) {
          // Questionnaire terminé → afficher les résultats
          setResults(resultData);
          setScreen('results');
        } else if (answersData && answersData.length > 0) {
          // Des réponses existent → reprendre le questionnaire
          setScreen('questionnaire');
        } else {
          // Aucune réponse → afficher l'intro
          setScreen('intro');
        }
      } catch (error) {
        console.error('Erreur lors de la vérification du statut:', error);
        setScreen('intro');
      }
    }
    checkStatus();
  }, []);

  useEffect(() => {
    (async () => {
      if (screen === 'questionnaire' && existingAnswers.length === 0) {
        await addAction({type: "JOURDA_SURVEY_STARTED"})
      }
    })();
  }, [screen]);

  const handleComplete = useCallback(async () => {
    // Afficher l'écran résultats avec un loader pendant le premier appel API
    setScreen('results');

    try {
      // Premier appel : créer le résultat (calcul des scores)
      const resultData = await createJourdaUserResult();
      setResults(resultData);
      await addAction({type: "JOURDA_SURVEY_COMPLETED"})
    } catch (error) {
      console.error('Erreur lors de la création des résultats:', error);
    }
  }, []);

  const handleRestart = useCallback(async () => {
    try {
      await resetJourdaSurvey();
      setResults(null);
      setExistingAnswers([]);
      setScreen('intro');
    } catch (error) {
      console.error('Erreur lors de la réinitialisation:', error);
    }
  }, []);

  if (screen === 'loading') {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  function handleStart() {
    if (!user?.is_premium) { setShowPremiumModal(true); return; }
    setScreen('questionnaire');
  }

  return (
    <div className="flex-1 flex bg-gray-900">
      {screen === 'intro' && (
        <IntroScreen
          prenom={prenom}
          onStart={handleStart}
          config={jourdaIntroConfig}
        />
      )}
      {screen === 'questionnaire' && (
        <QuestionScreen
          userId={user?.id}
          gender={user?.gender}
          questions={questions}
          initialAnswers={existingAnswers}
          onBack={() => setScreen('intro')}
          onComplete={handleComplete}
          config={jourdaQuestionConfig}
        />
      )}
      {screen === 'results' && (
        <JourdaResultsScreen
          results={results}
          onRestart={handleRestart}
        />
      )}
      <PremiumModal
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        askedForPremium={user?.asked_for_premium}
        onUpdateUser={updateUser}
        context="Ce questionnaire est réservé aux membres Premium"
        reason={QUIZZES_BY_ID.energies.premiumReason(user?.gender === 'boy' ? 'le' : 'la')}
      />
    </div>
  );
}
